import React, { useEffect, useState } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
} from "@tanstack/react-table";
import { ListFilter, ArrowDownUp, SkipForward, Clock, RefreshCw, BarChart, Table2, PieChart, List } from "lucide-react";
import SortDropdown from "./SortDropdown";
import CircleGraph from "./CircleGraph";

const styles = `
.view-toggle {
  background: #f8f9fa;
  border-radius: 6px;
  padding: 4px;
  display: inline-flex;
  gap: 4px;
}

.view-toggle button {
  padding: 6px 12px;
  border: none;
  background: transparent;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.3s ease;
}

.view-toggle button.active {
  background: white;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.view-toggle button:not(.active) {
  color: #6c757d;
}

.view-toggle button:hover:not(.active) {
  background: rgba(255,255,255,0.5);
}
`;

const ReusableTable = ({
  columns,
  data,
  graphData,
  onSearch,
  onDelete,
  onDuplicate,
  onSkip,
  onPostpone,
  onSync,
}) => {
    useEffect(() => {
        const styleSheet = document.createElement("style");
        styleSheet.innerText = styles;
        document.head.appendChild(styleSheet);
        return () => styleSheet.remove();
      }, []);
  const [searchValue, setSearchValue] = useState("");
  const [selectedRows, setSelectedRows] = useState({});
  const [isGraphView, setIsGraphView] = useState(false);
  const [sortModalOpen, setSortModalOpen] = useState(false);
  const [sortOrder, setSortOrder] = useState("asc");
  const [sortBy, setSortBy] = useState("Created");


  const table = useReactTable({
    data,
    columns: columns,
    state: {
      rowSelection: selectedRows,
    },
    onRowSelectionChange: (updater) => {
      const updatedSelection = updater instanceof Function ? updater(selectedRows) : updater;
      setSelectedRows(updatedSelection);
    },
    getRowId: (row) => row.name,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const selectedRowCount = Object.keys(selectedRows).length;

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchValue(value);
    onSearch(value);
  };

  const handleSort = (sortOption) => {
    switch (sortOption) {
      case 'newest':
        setSortOrder('desc');
        setSortBy('Created');
        break;
      case 'oldest':
        setSortOrder('asc');
        setSortBy('Created');
        break;
      default:
        setSortOrder('asc');
        setSortBy('Default');
    }
    setSortModalOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sortModalOpen && 
          !((event.target).closest('.sort-menu') || 
            (event.target).closest('.btn-light'))) {
        setSortModalOpen(false);
      }
    };
  
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [sortModalOpen]);

  return (
    <div className="table-container">
    <div className="table-toolbar d-flex justify-content-between align-items-center mb-3">
      <div className="toolbar-left">
        <div className="search-input-container">
          <input
            type="text"
            placeholder="Search..."
            value={searchValue}
            onChange={handleSearch}
            className="search-input form-control"
          />
        </div>
      </div>
      <div className="toolbar-right d-flex gap-2">
        <button className="btn btn-light">
          <ListFilter className="me-1" /> Filter
        </button>
        <div className="position-relative">
          <button
            className="btn btn-light"
            onClick={() => setSortModalOpen(!sortModalOpen)}
          >
            <ArrowDownUp className="me-1" /> Sort
          </button>
          {sortModalOpen && (
            <SortDropdown 
              isOpen={sortModalOpen}
              onSort={handleSort}
              onClose={() => setSortModalOpen(false)}
            />
          )}
        </div>

          
        <div className="view-toggle">
            <button
              onClick={() => setIsGraphView(false)}
              className={!isGraphView ? 'active' : ''}
              aria-label="Table view"
            >
              <List size={16} />
            </button>
            <button
              onClick={() => setIsGraphView(true)}
              className={isGraphView ? 'active' : ''}
              aria-label="Graph view"
            >
              <PieChart size={16} />
            </button>
          </div>
        </div>
      </div>


      {selectedRowCount > 0 && (
        <div className="action-buttons mb-3 d-flex justify-content-space-between align-items-center gap-2">
            <div className="d-flex justify-content-start align-items-center gap-3">
          <span className="selected-count ">
            <span className="fw-bold">{selectedRowCount}</span> selected
          </span>
          
          <button
            className="btn btn-outline-secondary"
            onClick={() => onDuplicate(Object.keys(selectedRows))}
          >
            Duplicate
          </button>
          
          <button
            className="btn btn-outline-danger"
            onClick={() => onDelete(Object.keys(selectedRows))}
          >
            Delete
          </button>
          </div>
          <div className="d-flex justify-content-start align-items-center gap-3">
          <button
            className="btn btn-outline-secondary"
            onClick={() => onSkip(Object.keys(selectedRows))}
          >
            <SkipForward size={16} className="me-1" />
            Skip
          </button>
          
          <button
            className="btn btn-outline-secondary"
            onClick={() => onPostpone(Object.keys(selectedRows))}
          >
            <Clock size={16} className="me-1" />
            Postpone
          </button>
          
          <button
            className="btn btn-outline-secondary"
            onClick={() => onSync(Object.keys(selectedRows))}
          >
            <RefreshCw size={16} className="me-1" />
            Sync
          </button>
          </div>
        </div>
      )}

      <hr />

      {isGraphView ? (
         <div >
         <CircleGraph data={graphData} />
       </div>
        
      ) : (
        <>
        <div className="table-responsive">
          <table className="table custom-table">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      onClick={header.column.getToggleSortingHandler()}
                      className="cursor-pointer"
                    >
                      <div className="d-flex align-items-center gap-1">
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {header.column.getIsSorted() && (
                          <ArrowDownUp size={14} />
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
          <div className="pagination d-flex justify-content-between align-items-center mt-3">
            <div className="rows-per-page d-flex align-items-center gap-2">
              <span>Show rows:</span>
              <select
                className="form-select"
                value={table.getState().pagination.pageSize}
                onChange={(e) => table.setPageSize(Number(e.target.value))}
              >
                {[10, 15, 25].map((pageSize) => (
                  <option key={pageSize} value={pageSize}>
                    {pageSize}
                  </option>
                ))}
              </select>
            </div>

            <div className="pagination-info">
              {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1}-
              {Math.min(
                (table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize,
                data.length
              )}{" "}
              of {data.length}
            </div>

            <div className="pagination-buttons d-flex gap-1">
              <button
                className="btn btn-outline-secondary"
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
              >
                {"<"}
              </button>
              <button
                className="btn btn-outline-secondary"
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
              >
                {">"}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ReusableTable;